import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Badge,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  Hotel,
  LocationOn,
  Business,
  KingBed,
  Domain,
  CheckBox,
  IndeterminateCheckBox,
  FilterList,
  WarningAmber,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface PropertySelectionModalProps {
  open: boolean;
  onClose: () => void;
  trendReportId: string;
  onConfirm: (selection: string, selectedProperties?: Property[]) => void;
  initialSelection?: string;
  preloadedData?: {
    properties?: Property[];
    propertiesByClass?: Record<string, Property[]>;
    submarkets?: Submarket[];
    continentMarkets?: Array<{
      marketId: number;
      marketName: string;
      countryName: string;
      propertyCount: number;
    }>;
    marketName?: string;
  };
}

interface Property {
  propertyId: string;
  propertyName: string;
  hotelClass: string;
  propertyAddress: string;
  brand: string;
  parentCompany?: string;
  rooms: number;
  yearBuilt: number;
  numberOfStories: number;
  constrStatus: string;
  hotelOperator: string;
  ownerName?: string;
  dataParticipation?: string;
  submarketId?: string;
  submarketName?: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface Submarket {
  submarketId: string | null;
  submarketName: string;
  propertyCount: number;
  totalRooms: number;
  brandCount: number;
  classCounts: {
    luxury: number;
    upperUpscale: number;
    upscale: number;
    upperMidscale: number;
    midscale: number;
    economy: number;
  };
}

const hotelClassColors: Record<string, string> = {
  'All': '#a6a6a6',           // RGB(166,166,166)
  'Luxury': '#003f9f',         // RGB(0,63,159)
  'Upper Upscale': '#16d9bc',  // RGB(22,217,188)
  'Upscale': '#0c7876',        // RGB(12,120,118)
  'Upper Midscale': '#32a8fd', // RGB(50,168,253)
  'Midscale': '#ea80ff',       // RGB(234,128,255)
  'Economy': '#7b2a5c',        // RGB(123,42,92)
  'Unclassified': '#808080'    // RGB(128,128,128)
};

export const PropertySelectionModal: React.FC<PropertySelectionModalProps> = ({
  open,
  onClose,
  trendReportId,
  onConfirm,
  initialSelection,
  preloadedData,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [tabValue, setTabValue] = useState(0);
  const [properties, setProperties] = useState<Property[]>([]);
  const [propertiesByClass, setPropertiesByClass] = useState<Record<string, Property[]>>({});
  const [submarkets, setSubmarkets] = useState<Submarket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  // Store full property objects with market info for cross-market selections
  const [allSelectedProperties, setAllSelectedProperties] = useState<Map<string, Property & { marketId: number; marketName: string }>>(new Map());
  const [selectedSubmarkets, setSelectedSubmarkets] = useState<Set<string>>(new Set());
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const [marketName, setMarketName] = useState('');
  const [dataParticipantsOnly, setDataParticipantsOnly] = useState(true);
  const [submarketFilter, setSubmarketFilter] = useState<string>('all');
  const [availableMarkets, setAvailableMarkets] = useState<Array<{
    marketId: number;
    marketName: string;
    countryName: string;
    propertyCount: number;
  }>>([]);
  const [selectedMarket, setSelectedMarket] = useState<number | null>(null);
  const [trendReportMarketId, setTrendReportMarketId] = useState<number | null>(null);
  const [showSelectionSummary, setShowSelectionSummary] = useState(false);
  const [showRoomDistribution, setShowRoomDistribution] = useState(true);

  // Warning modal states
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({
    open: false,
    title: '',
    message: '',
  });

  // Fetch available markets when modal opens
  useEffect(() => {
    if (open && trendReportId) {
      fetchAvailableMarkets();
    }
  }, [open, trendReportId]);

  // Fetch data when selected market changes
  useEffect(() => {
    if (selectedMarket !== null) {
      fetchData(selectedMarket);
    }
  }, [selectedMarket]);

  const fetchAvailableMarkets = async () => {
    // Use preloaded data if available
    if (preloadedData?.continentMarkets && preloadedData.continentMarkets.length > 0) {
      console.log('Using preloaded continent markets:', preloadedData.continentMarkets);
      setAvailableMarkets(preloadedData.continentMarkets);
      // Find the trend report's market ID from the preloaded data
      const trendReportMarket = preloadedData.continentMarkets.find(m =>
        m.marketName === preloadedData.marketName
      );
      if (trendReportMarket) {
        setTrendReportMarketId(trendReportMarket.marketId);
        setSelectedMarket(trendReportMarket.marketId);
      }
      return;
    }

    // Otherwise, fetch from API
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/continent-markets`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        console.log('Available markets received:', data);
        setAvailableMarkets(data.markets || []);
        setTrendReportMarketId(data.trendReportMarketId);
        setSelectedMarket(data.trendReportMarketId);
      } else {
        console.error('Failed to fetch available markets:', response?.status);
        setError('Failed to load available markets');
      }
    } catch (err) {
      console.error('Error fetching available markets:', err);
      setError('Failed to load available markets');
    }
  };

  const fetchData = async (marketId: number) => {
    // Check if this is the initial market and we have preloaded data
    const isInitialMarket = marketId === trendReportMarketId;
    const hasPreloadedProperties = preloadedData?.properties && preloadedData.properties.length > 0;
    const hasPreloadedSubmarkets = preloadedData?.submarkets && preloadedData.submarkets.length >= 0;

    if (isInitialMarket && hasPreloadedProperties && hasPreloadedSubmarkets) {
      console.log('Using preloaded properties and submarkets for initial market');
      setProperties(preloadedData.properties!);
      setPropertiesByClass(preloadedData.propertiesByClass || {});
      setMarketName(preloadedData.marketName || '');
      setSubmarkets(preloadedData.submarkets!);

      // Sync selectedProperties with allSelectedProperties for current market
      const currentMarketSelectedIds = new Set<string>();
      allSelectedProperties.forEach((prop, key) => {
        if (prop.marketId === marketId) {
          currentMarketSelectedIds.add(key);
        }
      });
      setSelectedProperties(currentMarketSelectedIds);
      return;
    }

    // Otherwise, fetch from API
    setLoading(true);
    setError(null);

    try {
      // Fetch properties - pass marketId as query parameter
      const propertiesResponse = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/market-properties?marketId=${marketId}`,
        { method: 'GET' }
      );

      if (propertiesResponse?.ok) {
        const propertiesData = await propertiesResponse.json();
        console.log('Properties data received:', propertiesData);
        setProperties(propertiesData.properties || []);
        setPropertiesByClass(propertiesData.propertiesByClass || {});
        setMarketName(propertiesData.marketName || '');

        // Sync selectedProperties with allSelectedProperties for current market
        const currentMarketSelectedIds = new Set<string>();
        allSelectedProperties.forEach((prop, key) => {
          if (prop.marketId === marketId) {
            currentMarketSelectedIds.add(key);
          }
        });
        setSelectedProperties(currentMarketSelectedIds);
      } else {
        console.error('Properties response not ok:', propertiesResponse?.status);
      }

      // Fetch submarkets - pass marketId as query parameter
      const submarketsResponse = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/submarkets?marketId=${marketId}`,
        { method: 'GET' }
      );

      if (submarketsResponse?.ok) {
        const submarketsData = await submarketsResponse.json();
        console.log('Submarkets data received:', submarketsData);
        setSubmarkets(submarketsData.submarkets || []);
      } else {
        console.error('Submarkets response not ok:', submarketsResponse?.status);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load properties and submarkets');
    } finally {
      setLoading(false);
    }
  };

  const toggleClass = (className: string) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(className)) {
      newExpanded.delete(className);
    } else {
      newExpanded.add(className);
    }
    setExpandedClasses(newExpanded);
  };

  const toggleAllInClass = (className: string) => {
    const classProperties = propertiesByClass[className] || [];
    const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
    const allSelected = classProperties.every(p => selectedProperties.has(getPropertyKey(p)));

    const newSelected = new Set(selectedProperties);
    const newAllSelected = new Map(allSelectedProperties);

    if (allSelected) {
      // Remove all properties from both sets
      classProperties.forEach(p => {
        const key = getPropertyKey(p);
        newSelected.delete(key);
        newAllSelected.delete(key);
      });
    } else {
      // Add all properties to both sets
      classProperties.forEach(p => {
        const key = getPropertyKey(p);
        newSelected.add(key);
        if (selectedMarket) {
          newAllSelected.set(key, {
            ...p,
            marketId: selectedMarket,
            marketName: marketName
          });
        }
      });

      // Clear submarkets when selecting properties (only if multiple submarkets exist)
      if (selectedSubmarkets.size > 0 && submarkets.length > 1) {
        setSelectedSubmarkets(new Set());
      }
    }
    setSelectedProperties(newSelected);
    setAllSelectedProperties(newAllSelected);
  };

  const validateRoomDistribution = (): { valid: boolean; errorMessage?: string } => {
    // Only consider data participants for room distribution requirements
    const selectedProps = Array.from(allSelectedProperties.values()).filter(p => p.dataParticipation != null);

    if (selectedProps.length === 0) {
      return { valid: true };
    }

    // Check minimum properties requirement
    if (selectedProps.length < 4) {
      return {
        valid: false,
        errorMessage: `You have selected ${selectedProps.length} ${selectedProps.length === 1 ? 'property' : 'properties'}. A minimum of 4 properties is required for the report.`
      };
    }

    // Calculate total rooms
    const totalRooms = selectedProps.reduce((sum, p) => sum + (p.rooms || 0), 0);

    if (totalRooms === 0) {
      return { valid: true };
    }

    // Check if any single property has more than 50% of rooms
    for (const prop of selectedProps) {
      const roomPercentage = ((prop.rooms || 0) / totalRooms) * 100;
      if (roomPercentage > 50) {
        return {
          valid: false,
          errorMessage: `Property "${prop.propertyName}" has ${prop.rooms} rooms (${roomPercentage.toFixed(1)}% of total), which exceeds the 50% limit. No single property can have more than 50% of the total rooms.`
        };
      }
    }

    // Check parent company distribution (excluding NULL parent companies)
    const parentCompanyRooms = new Map<string, number>();
    for (const prop of selectedProps) {
      if (prop.parentCompany) {
        const current = parentCompanyRooms.get(prop.parentCompany) || 0;
        parentCompanyRooms.set(prop.parentCompany, current + (prop.rooms || 0));
      }
    }

    for (const [company, rooms] of Array.from(parentCompanyRooms.entries())) {
      const roomPercentage = (rooms / totalRooms) * 100;
      if (roomPercentage > 70) {
        return {
          valid: false,
          errorMessage: `Parent company "${company}" has ${rooms} rooms (${roomPercentage.toFixed(1)}% of total), which exceeds the 70% limit. No single parent company can have more than 70% of the total rooms.`
        };
      }
    }

    // Check brand distribution (excluding NULL brands)
    const brandRooms = new Map<string, number>();
    for (const prop of selectedProps) {
      if (prop.brand) {
        const current = brandRooms.get(prop.brand) || 0;
        brandRooms.set(prop.brand, current + (prop.rooms || 0));
      }
    }

    for (const [brand, rooms] of Array.from(brandRooms.entries())) {
      const roomPercentage = (rooms / totalRooms) * 100;
      if (roomPercentage > 50) {
        return {
          valid: false,
          errorMessage: `Brand "${brand}" has ${rooms} rooms (${roomPercentage.toFixed(1)}% of total), which exceeds the 50% limit. No single brand can have more than 50% of the total rooms.`
        };
      }
    }

    return { valid: true };
  };

  const toggleProperty = (propertyId: string) => {
    const newSelected = new Set(selectedProperties);
    const newAllSelected = new Map(allSelectedProperties);

    if (newSelected.has(propertyId)) {
      // Remove from both sets
      newSelected.delete(propertyId);
      newAllSelected.delete(propertyId);
    } else {
      // Add to both sets
      newSelected.add(propertyId);
      const property = properties.find(p => {
        const key = p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
        return key === propertyId;
      });
      if (property && selectedMarket) {
        newAllSelected.set(propertyId, {
          ...property,
          marketId: selectedMarket,
          marketName: marketName
        });
      }
    }

    setSelectedProperties(newSelected);
    setAllSelectedProperties(newAllSelected);

    // Clear submarkets when selecting properties (only if multiple submarkets exist)
    if (selectedSubmarkets.size > 0 && submarkets.length > 1) {
      setSelectedSubmarkets(new Set());
    }
  };

  const toggleSubmarket = (submarketName: string) => {
    // Warn if properties are selected
    if (selectedProperties.size > 0) {
      setWarningDialog({
        open: true,
        title: 'Clear Selected Properties?',
        message: 'Selecting submarkets will clear all selected properties. Do you want to continue?',
        confirmText: 'Yes, Clear Properties',
        cancelText: 'Cancel',
        onConfirm: () => {
          setSelectedProperties(new Set());
          const newSelected = new Set(selectedSubmarkets);
          if (newSelected.has(submarketName)) {
            newSelected.delete(submarketName);
          } else {
            newSelected.add(submarketName);
          }
          setSelectedSubmarkets(newSelected);
        }
      });
      return;
    }

    // Toggle the submarket selection
    const newSelected = new Set(selectedSubmarkets);
    if (newSelected.has(submarketName)) {
      newSelected.delete(submarketName);
    } else {
      newSelected.add(submarketName);
    }
    setSelectedSubmarkets(newSelected);
  };

  const getFilteredProperties = () => {
    let filtered = properties;

    // Filter by search term - only hotel name or address
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p =>
        p.propertyName.toLowerCase().includes(searchLower) ||
        p.propertyAddress?.toLowerCase().includes(searchLower)
      );
    }

    // Filter to data participants only
    if (dataParticipantsOnly) {
      filtered = filtered.filter(p => p.dataParticipation != null);
    }

    // Filter by submarket if selected
    if (submarketFilter !== 'all') {
      filtered = filtered.filter(p => p.submarketName === submarketFilter);
    }

    return filtered;
  };

  const formatSelection = () => {
    // Check if properties are selected
    if (allSelectedProperties.size > 0) {
      const selectedProps = Array.from(allSelectedProperties.values());

      // Group by market
      const byMarket = selectedProps.reduce((acc, p) => {
        if (!acc[p.marketName]) {
          acc[p.marketName] = [];
        }
        acc[p.marketName].push(p);
        return acc;
      }, {} as Record<string, typeof selectedProps>);

      let formatted = '';
      Object.entries(byMarket).forEach(([marketName, props]) => {
        formatted += `\n${marketName} (${props.length} ${props.length === 1 ? 'hotel' : 'hotels'}):\n`;
        props.forEach((p, index) => {
          formatted += `  ${index + 1}. ${p.propertyName} - ${p.propertyAddress} - ${p.rooms} rooms${p.parentCompany ? ` - ${p.parentCompany}` : ''}${p.brand ? ` - ${p.brand}` : ''}\n`;
        });
      });

      return `SELECTED PROPERTIES (${selectedProps.length} hotels across ${Object.keys(byMarket).length} ${Object.keys(byMarket).length === 1 ? 'market' : 'markets'}):${formatted}`;
    }

    // Check if submarkets are selected
    if (selectedSubmarkets.size > 0) {
      const selectedSubs = submarkets.filter(s => selectedSubmarkets.has(s.submarketName));

      const formatted = selectedSubs.map((s, index) =>
        `${index + 1}. ${s.submarketName} - ${s.propertyCount} hotels, ${s.totalRooms} total rooms`
      ).join('\n');

      return `SELECTED SUBMARKETS (${selectedSubs.length}):\n\n${formatted}`;
    }

    setWarningDialog({
      open: true,
      title: 'No Selection',
      message: 'Please select either properties or submarkets',
    });
    return null;
  };

  const handleConfirm = () => {
    // Validate room distribution if properties are selected
    if (allSelectedProperties.size > 0) {
      const validation = validateRoomDistribution();
      if (!validation.valid) {
        const detailedMessage = `${validation.errorMessage}\n\n` +
          `Not meeting these distribution requirements significantly increases the likelihood that CoStar will reject your competitive set. ` +
          `If CoStar rejects your selection, you will need to choose additional properties and resubmit your request, which will delay your report delivery by at least 1-2 business days.\n\n` +
          `We strongly recommend adjusting your property selection now to meet all requirements. However, you may proceed with your current selection if you accept the risk of potential delays.`;

        setWarningDialog({
          open: true,
          title: 'Distribution Requirements Not Met',
          message: detailedMessage,
          confirmText: 'Proceed Anyway',
          cancelText: 'Adjust Properties',
          onConfirm: () => {
            // User wants to proceed despite not meeting requirements
            const selection = formatSelection();
            if (selection) {
              onConfirm(selection, Array.from(allSelectedProperties.values()));
              onClose();
            }
          }
        });
        return;
      }
    }

    const selection = formatSelection();
    if (selection) {
      onConfirm(selection, allSelectedProperties.size > 0 ? Array.from(allSelectedProperties.values()) : undefined);
      onClose();
    }
  };

  const getPropertyKey = (p: Property) => p.propertyId || `${p.propertyName}-${p.propertyAddress}`;

  const totalRoomsSelected = Array.from(allSelectedProperties.values())
    .reduce((sum, p) => sum + (p.rooms || 0), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Select Properties {marketName && `- ${marketName}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {submarkets.length > 1
            ? 'Choose either individual properties OR submarkets (cannot mix both)'
            : 'Choose individual properties'}
        </Typography>
        {availableMarkets.length > 1 && (
          <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
            You can select properties from any market in the same continent
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {(selectedProperties.size > 0 && selectedSubmarkets.size > 0 && submarkets.length > 1) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Selection Conflict:</strong> You cannot mix properties and submarkets. Please clear one selection before proceeding.
              </Alert>
            )}

            {submarkets.length > 1 ? (
              <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 2, flexShrink: 0 }}>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Properties
                      <Badge badgeContent={selectedProperties.size} color="primary" />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Submarkets
                      <Badge badgeContent={selectedSubmarkets.size} color="primary" />
                    </Box>
                  }
                />
              </Tabs>
            ) : null}

            {/* Properties Tab - Show when tab is 0 or when there's only 1 or no submarkets */}
            {(tabValue === 0 || submarkets.length <= 1) && (
              <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: '100%' }}>
                {/* Left Column - Properties List */}
                <Grid item xs={12} md={showRoomDistribution ? 8 : 12} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                  {/* Market Selector */}
                  {availableMarkets.length > 1 && (
                    <Box sx={{ mb: 2, flexShrink: 0 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Select Market"
                        value={selectedMarket || ''}
                        onChange={(e) => {
                          const newMarketId = parseInt(e.target.value);
                          setSelectedMarket(newMarketId);
                          // Don't clear allSelectedProperties - keep selections across markets
                          // selectedProperties will be synced in fetchData
                          setSearchTerm('');
                          setSubmarketFilter('all');
                        }}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        {availableMarkets.map(market => (
                          <option key={market.marketId} value={market.marketId}>
                            {market.marketName} ({market.countryName}) - {market.propertyCount} properties
                            {market.marketId === trendReportMarketId ? ' (Original Market)' : ''}
                          </option>
                        ))}
                      </TextField>
                    </Box>
                  )}

                  {/* Selection Summary */}
                  {allSelectedProperties.size > 0 && (
                    <Box sx={{ mb: 2, flexShrink: 0 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: 'primary.50',
                          borderColor: 'primary.main',
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowSelectionSummary(!showSelectionSummary)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                              Selected: {allSelectedProperties.size} {allSelectedProperties.size === 1 ? 'property' : 'properties'} • {totalRoomsSelected.toLocaleString()} rooms
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Set(Array.from(allSelectedProperties.values()).map(p => p.marketName)).size} {new Set(Array.from(allSelectedProperties.values()).map(p => p.marketName)).size === 1 ? 'market' : 'markets'}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSelectionSummary(!showSelectionSummary);
                            }}
                          >
                            {showSelectionSummary ? 'Hide Details' : 'Show Details'}
                          </Button>
                        </Box>

                        <Collapse in={showSelectionSummary}>
                          <Box sx={{ mt: 2, maxHeight: '200px', overflowY: 'auto' }}>
                            {(() => {
                              const byMarket = Array.from(allSelectedProperties.values()).reduce((acc, p) => {
                                if (!acc[p.marketName]) {
                                  acc[p.marketName] = [];
                                }
                                acc[p.marketName].push(p);
                                return acc;
                              }, {} as Record<string, Array<Property & { marketId: number; marketName: string }>>);

                              return Object.entries(byMarket).map(([marketName, props]) => (
                                <Box key={marketName} sx={{ mb: 2 }}>
                                  <Typography variant="caption" fontWeight="bold" color="primary">
                                    {marketName} ({props.length})
                                  </Typography>
                                  <List dense sx={{ pl: 2 }}>
                                    {props.map(p => {
                                      const key = p.propertyId || `${p.propertyName}-${p.propertyAddress}`;
                                      return (
                                        <ListItem key={key} sx={{ py: 0.25 }}>
                                          <ListItemText
                                            primaryTypographyProps={{ variant: 'caption' }}
                                            primary={`${p.propertyName} - ${p.rooms} rooms`}
                                          />
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                </Box>
                              ));
                            })()}
                          </Box>
                        </Collapse>
                      </Paper>
                    </Box>
                  )}

                  <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by hotel name or address..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dataParticipantsOnly}
                          onChange={(e) => setDataParticipantsOnly(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Data Participants Only"
                      sx={{ whiteSpace: 'nowrap' }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Submarket"
                      value={submarketFilter}
                      onChange={(e) => setSubmarketFilter(e.target.value)}
                      sx={{ minWidth: 200 }}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="all">All Submarkets</option>
                      {submarkets.map(s => (
                        <option key={s.submarketId || s.submarketName} value={s.submarketName}>
                          {s.submarketName} ({s.propertyCount})
                        </option>
                      ))}
                    </TextField>
                    {!showRoomDistribution && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowRoomDistribution(true)}
                        startIcon={<FilterList />}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        Show Requirements
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
                    {/* Loading overlay for properties list */}
                    {loading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.8)',
                          zIndex: 10
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}
                  {(() => {
                    const filteredProperties = getFilteredProperties();

                    if (filteredProperties.length === 0 && searchTerm) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties found matching "{searchTerm}"
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Try adjusting your search terms or clearing the search box
                          </Typography>
                        </Box>
                      );
                    }

                    if (properties.length === 0) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties available for this market
                          </Typography>
                        </Box>
                      );
                    }

                    // If no filtered properties after applying filters
                    if (filteredProperties.length === 0) {
                      return (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                          <Typography variant="body1" color="text.secondary">
                            No properties match the current filters
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Try clearing the search or changing the submarket filter
                          </Typography>
                        </Box>
                      );
                    }

                    return Object.entries(propertiesByClass).map(([className, classProps]) => {
                      // Filter class properties based on filtered properties
                      const filteredPropertyKeys = new Set(filteredProperties.map(p => getPropertyKey(p)));
                      const filteredClassProps = classProps.filter(p =>
                        filteredPropertyKeys.has(getPropertyKey(p))
                      );

                      if (filteredClassProps.length === 0) return null;

                      const allSelected = filteredClassProps.every(p => selectedProperties.has(getPropertyKey(p)));
                      const someSelected = filteredClassProps.some(p => selectedProperties.has(getPropertyKey(p)));

                    return (
                      <Paper key={className} sx={{ mb: 2, p: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                            p: 1,
                          }}
                          onClick={() => toggleClass(className)}
                        >
                          <IconButton size="small">
                            {expandedClasses.has(className) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={!allSelected && someSelected}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAllInClass(className);
                            }}
                          />
                          <Chip
                            label={className}
                            size="small"
                            sx={{
                              bgcolor: hotelClassColors[className] || hotelClassColors['Unclassified'],
                              color: 'white',
                              fontWeight: 'bold',
                              mr: 2,
                            }}
                          />
                          <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {filteredClassProps.length} properties • {filteredClassProps.reduce((sum, p) => sum + (p.rooms || 0), 0)} rooms
                          </Typography>
                        </Box>

                        <Collapse in={expandedClasses.has(className)}>
                          <List dense sx={{ pl: 4 }}>
                            {filteredClassProps.map(property => {
                              const propertyKey = property.propertyId || `${property.propertyName}-${property.propertyAddress}`;
                              return (
                              <ListItem key={propertyKey} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedProperties.has(propertyKey)}
                                    onChange={() => toggleProperty(propertyKey)}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primaryTypographyProps={{ component: 'div' }}
                                  secondaryTypographyProps={{ component: 'div' }}
                                  primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" fontWeight="medium">
                                        {property.propertyName}
                                      </Typography>
                                      {property.dataParticipation && (
                                        <Chip
                                          label="Data Participant"
                                          size="small"
                                          sx={{
                                            backgroundColor: '#FF8F00',
                                            color: 'white',
                                            fontWeight: 'bold',
                                          }}
                                        />
                                      )}
                                      {property.parentCompany && (
                                        <Chip
                                          label={property.parentCompany}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: 'rgb(123,42,92)',
                                            color: 'rgb(123,42,92)',
                                            '&:hover': {
                                              backgroundColor: 'rgba(123,42,92,0.08)'
                                            }
                                          }}
                                        />
                                      )}
                                      {property.brand && (
                                        <Chip
                                          label={property.brand}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: 'rgb(0,80,173)',
                                            color: 'rgb(0,80,173)',
                                            '&:hover': {
                                              backgroundColor: 'rgba(0,80,173,0.08)'
                                            }
                                          }}
                                        />
                                      )}
                                      <Chip
                                        label={`${property.rooms} rooms`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          backgroundColor: 'white',
                                          color: 'rgb(12, 120, 118)',
                                          borderColor: 'rgb(12, 120, 118)'
                                        }}
                                      />
                                    </Box>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" component="span">
                                        {property.propertyAddress}
                                      </Typography>
                                      {property.submarketName && (
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                          • {property.submarketName}
                                        </Typography>
                                      )}
                                      {property.hotelOperator && (
                                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                                          • Operated by {property.hotelOperator}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      </Paper>
                    );
                  });
                })()}
                  </Box>
                </Grid>

                {/* Right Column - Room Distribution Requirements */}
                {showRoomDistribution && (
                  <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
                  <Paper
                    elevation={3}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => setShowRoomDistribution(false)}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        Room Distribution Requirements
                      </Typography>
                      <IconButton size="small" sx={{ color: 'white' }}>
                        <ExpandLess />
                      </IconButton>
                    </Box>
                    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                        {(() => {
                          if (selectedProperties.size === 0) {
                            return (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  To obtain the required data from CoStar, your property selection must meet the following criteria. If additional requirements arise, we'll contact you to discuss the best path forward.
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                                    <Box component="span" sx={{ mr: 1 }}>•</Box>
                                    <span>At least 4 properties must be selected</span>
                                  </Typography>
                                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                                    <Box component="span" sx={{ mr: 1 }}>•</Box>
                                    <span>No single property can have more than 50% of the total rooms</span>
                                  </Typography>
                                  <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                                    <Box component="span" sx={{ mr: 1 }}>•</Box>
                                    <span>No single parent company can have more than 70% of the total rooms</span>
                                  </Typography>
                                  <Typography variant="body2" component="div" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Box component="span" sx={{ mr: 1 }}>•</Box>
                                    <span>No single brand can have more than 50% of the total rooms</span>
                                  </Typography>
                                </Box>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    Select properties to see distribution analysis
                                  </Typography>
                                </Alert>
                              </Box>
                            );
                          }

                          // Only consider data participants for room distribution requirements
                          const selectedProps = properties.filter(p => selectedProperties.has(getPropertyKey(p)) && p.dataParticipation != null);
                          const totalRooms = selectedProps.reduce((sum, p) => sum + (p.rooms || 0), 0);

                          // Check minimum properties requirement
                          const hasMinimumProperties = selectedProps.length >= 4;

                          // Calculate property with highest room percentage
                          let maxPropertyRooms = 0;
                          let maxPropertyName = '';
                          for (const prop of selectedProps) {
                            if ((prop.rooms || 0) > maxPropertyRooms) {
                              maxPropertyRooms = prop.rooms || 0;
                              maxPropertyName = prop.propertyName;
                            }
                          }
                          const maxPropertyPercentage = totalRooms > 0 ? (maxPropertyRooms / totalRooms) * 100 : 0;

                          // Calculate parent company distribution
                          const parentCompanyRooms = new Map<string, number>();
                          for (const prop of selectedProps) {
                            if (prop.parentCompany) {
                              const current = parentCompanyRooms.get(prop.parentCompany) || 0;
                              parentCompanyRooms.set(prop.parentCompany, current + (prop.rooms || 0));
                            }
                          }

                          let maxCompanyRooms = 0;
                          let maxCompanyName = '';
                          for (const [company, rooms] of Array.from(parentCompanyRooms.entries())) {
                            if (rooms > maxCompanyRooms) {
                              maxCompanyRooms = rooms;
                              maxCompanyName = company;
                            }
                          }
                          const maxCompanyPercentage = totalRooms > 0 ? (maxCompanyRooms / totalRooms) * 100 : 0;

                          // Calculate brand distribution
                          const brandRooms = new Map<string, number>();
                          for (const prop of selectedProps) {
                            if (prop.brand) {
                              const current = brandRooms.get(prop.brand) || 0;
                              brandRooms.set(prop.brand, current + (prop.rooms || 0));
                            }
                          }

                          let maxBrandRooms = 0;
                          let maxBrandName = '';
                          for (const [brand, rooms] of Array.from(brandRooms.entries())) {
                            if (rooms > maxBrandRooms) {
                              maxBrandRooms = rooms;
                              maxBrandName = brand;
                            }
                          }
                          const maxBrandPercentage = totalRooms > 0 ? (maxBrandRooms / totalRooms) * 100 : 0;

                          const propertyExceedsLimit = maxPropertyPercentage > 50;
                          const companyExceedsLimit = maxCompanyPercentage > 70;
                          const brandExceedsLimit = maxBrandPercentage > 50;
                          const allCriteriaMet = hasMinimumProperties && !propertyExceedsLimit && !companyExceedsLimit && !brandExceedsLimit;

                          return (
                            <Box>
                              <Alert
                                severity={allCriteriaMet ? "success" : !hasMinimumProperties || propertyExceedsLimit || companyExceedsLimit || brandExceedsLimit ? "warning" : "info"}
                                sx={{ mb: 2 }}
                              >
                                <Typography variant="body2" fontWeight="bold">
                                  {allCriteriaMet ? "✓ All Requirements Met" : "Analysis"}
                                </Typography>
                              </Alert>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Your property selection must meet the following criteria to obtain data from CoStar:
                              </Typography>
                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color={allCriteriaMet ? "success.main" : !hasMinimumProperties ? "error.main" : "text.secondary"}
                                  sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <Box component="span" sx={{ mr: 1 }}>
                                    {hasMinimumProperties ? "✓" : "✗"}
                                  </Box>
                                  <span>
                                    Data-participating properties: <strong>{selectedProps.length}</strong>
                                    {!hasMinimumProperties && <Box component="span" sx={{ display: 'block', fontSize: '0.85em', color: 'error.main' }}>Minimum of 4 required</Box>}
                                  </span>
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color={allCriteriaMet ? "success.main" : propertyExceedsLimit ? "error.main" : "text.secondary"}
                                  sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <Box component="span" sx={{ mr: 1 }}>
                                    {!propertyExceedsLimit ? "✓" : "✗"}
                                  </Box>
                                  <Box>
                                    <Box>Highest single data-participating property:</Box>
                                    <Box sx={{ fontSize: '0.85em', mt: 0.5 }}>
                                      "{maxPropertyName}"
                                    </Box>
                                    <Box sx={{ fontSize: '0.85em', fontWeight: 'bold' }}>
                                      {maxPropertyRooms} rooms ({maxPropertyPercentage.toFixed(1)}%)
                                    </Box>
                                    {propertyExceedsLimit && (
                                      <Box component="span" sx={{ display: 'block', fontSize: '0.85em', color: 'error.main' }}>
                                        Exceeds 50% limit
                                      </Box>
                                    )}
                                  </Box>
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color={allCriteriaMet ? "success.main" : companyExceedsLimit ? "error.main" : "text.secondary"}
                                  sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <Box component="span" sx={{ mr: 1 }}>
                                    {!companyExceedsLimit ? "✓" : "✗"}
                                  </Box>
                                  <Box>
                                    {maxCompanyName ? (
                                      <>
                                        <Box>Highest parent company:</Box>
                                        <Box sx={{ fontSize: '0.85em', mt: 0.5 }}>
                                          "{maxCompanyName}"
                                        </Box>
                                        <Box sx={{ fontSize: '0.85em', fontWeight: 'bold' }}>
                                          {maxCompanyRooms} rooms ({maxCompanyPercentage.toFixed(1)}%)
                                        </Box>
                                        {companyExceedsLimit && (
                                          <Box component="span" sx={{ display: 'block', fontSize: '0.85em', color: 'error.main' }}>
                                            Exceeds 70% limit
                                          </Box>
                                        )}
                                      </>
                                    ) : (
                                      <Box>No parent company concentration (Unknown owners excluded)</Box>
                                    )}
                                  </Box>
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                  variant="body2"
                                  component="div"
                                  color={allCriteriaMet ? "success.main" : brandExceedsLimit ? "error.main" : "text.secondary"}
                                  sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <Box component="span" sx={{ mr: 1 }}>
                                    {!brandExceedsLimit ? "✓" : "✗"}
                                  </Box>
                                  <Box>
                                    {maxBrandName ? (
                                      <>
                                        <Box>Highest brand:</Box>
                                        <Box sx={{ fontSize: '0.85em', mt: 0.5 }}>
                                          "{maxBrandName}"
                                        </Box>
                                        <Box sx={{ fontSize: '0.85em', fontWeight: 'bold' }}>
                                          {maxBrandRooms} rooms ({maxBrandPercentage.toFixed(1)}%)
                                        </Box>
                                        {brandExceedsLimit && (
                                          <Box component="span" sx={{ display: 'block', fontSize: '0.85em', color: 'error.main' }}>
                                            Exceeds 50% limit
                                          </Box>
                                        )}
                                      </>
                                    ) : (
                                      <Box>No brand concentration (Unbranded properties excluded)</Box>
                                    )}
                                  </Box>
                                </Typography>
                              </Box>
                              {(!hasMinimumProperties || propertyExceedsLimit || companyExceedsLimit || brandExceedsLimit) && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    Consider adjusting your selection to meet all criteria
                                  </Typography>
                                </Alert>
                              )}
                            </Box>
                          );
                        })()}
                    </Box>
                  </Paper>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Submarkets Tab - Only show when there are multiple submarkets */}
            {tabValue === 1 && submarkets.length > 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexShrink: 0 }}>
                  <Alert severity="info" sx={{ flex: 1 }}>
                    {selectedSubmarkets.size > 0
                      ? `${selectedSubmarkets.size} submarket${selectedSubmarkets.size > 1 ? 's' : ''} selected`
                      : '0 submarkets selected'
                    }
                    {selectedProperties.size > 0 && ' • Note: Properties will be cleared if you select submarkets'}
                  </Alert>
                  {selectedSubmarkets.size > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedSubmarkets(new Set())}
                      color="error"
                    >
                      Clear All
                    </Button>
                  )}
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, position: 'relative' }}>
                  {/* Loading overlay for submarkets list */}
                  {loading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <Grid container spacing={2}>
                  {submarkets.map(submarket => (
                    <Grid item xs={12} md={6} key={submarket.submarketId || submarket.submarketName}>
                      <Card
                        sx={{
                          border: selectedSubmarkets.has(submarket.submarketName)
                            ? '2px solid primary.main'
                            : '1px solid',
                          borderColor: selectedSubmarkets.has(submarket.submarketName)
                            ? 'primary.main'
                            : 'divider',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox
                              checked={selectedSubmarkets.has(submarket.submarketName)}
                              onChange={() => toggleSubmarket(submarket.submarketName)}
                            />
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                              {submarket.submarketName}
                            </Typography>
                          </Box>

                          <Grid container spacing={1} sx={{ mb: 1 }}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Properties</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.propertyCount}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Total Rooms</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.totalRooms.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">Brands</Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {submarket.brandCount}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Typography variant="caption" color="text.secondary">Hotel Classes:</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {Object.entries(submarket.classCounts).map(([className, count]) => {
                              if (count === 0) return null;
                              const displayName = className.charAt(0).toUpperCase() + className.slice(1).replace(/([A-Z])/g, ' $1');
                              return (
                                <Chip
                                  key={className}
                                  label={`${displayName}: ${count}`}
                                  size="small"
                                  sx={{
                                    bgcolor: hotelClassColors[displayName] || hotelClassColors['Unclassified'],
                                    color: 'white',
                                    fontSize: '0.7rem',
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  </Grid>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {allSelectedProperties.size > 0 && (
            <Button
              onClick={() => {
                setAllSelectedProperties(new Map());
                setSelectedProperties(new Set());
              }}
              color="error"
            >
              Clear All
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={allSelectedProperties.size === 0 && selectedSubmarkets.size === 0}
          >
            Confirm Selection {
              allSelectedProperties.size > 0
                ? `(${allSelectedProperties.size} properties)`
                : selectedSubmarkets.size > 0
                  ? `(${selectedSubmarkets.size} submarket${selectedSubmarkets.size > 1 ? 's' : ''})`
                  : ''
            }
          </Button>
        </Box>
      </DialogActions>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialog.open}
        onClose={() => setWarningDialog({ ...warningDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" />
          {warningDialog.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {warningDialog.onConfirm ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current Issue:
                  </Typography>
                  <Typography variant="body2">
                    {warningDialog.message?.split('\n\n')[0]}
                  </Typography>
                </Alert>

                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Potential Consequences:
                  </Typography>
                  <Typography variant="body2">
                    {warningDialog.message?.split('\n\n')[1]}
                  </Typography>
                </Alert>

                <Typography variant="body2" sx={{ mt: 2 }}>
                  {warningDialog.message?.split('\n\n')[2]}
                </Typography>
              </>
            ) : (
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{warningDialog.message}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setWarningDialog({ ...warningDialog, open: false })}
            variant={warningDialog.onConfirm ? "contained" : "contained"}
            color="primary"
            sx={{ minWidth: 140 }}
          >
            {warningDialog.cancelText || 'OK'}
          </Button>
          {warningDialog.onConfirm && (
            <Button
              onClick={() => {
                warningDialog.onConfirm!();
                setWarningDialog({ ...warningDialog, open: false });
              }}
              variant="outlined"
              color="warning"
              sx={{ minWidth: 140 }}
            >
              {warningDialog.confirmText || 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};